import { useRef, useCallback } from 'react';
import type { CanvasObject } from '../types';
import { useImageRegistry } from './useImageRegistry';

interface UseClipboardProps {
  selectedId: string | null;
  getObjectById: (id: string | null) => CanvasObject | null;
  addObject: (obj: CanvasObject) => void;
  setSelectedId: (id: string | null) => void;
  imageRegistry: ReturnType<typeof useImageRegistry>;
  requestRender: () => void;
  // 다중 선택 관련 파라미터
  selectedIds?: Set<string>;
  getSelectedObjects?: () => CanvasObject[];
  setSelection?: (ids: string[]) => void;
  zOrder: string[];
  setZOrder: React.Dispatch<React.SetStateAction<string[]>>;
  setObjects: React.Dispatch<React.SetStateAction<Map<string, CanvasObject>>>;
  // 삭제 관련 파라미터
  deleteObject: (id: string) => void;
  clearSelection?: () => void;
  // 클리핑 마스크 맵 (마스크/클리핑 레이어 그룹 확인용)
  clipMaskMap?: {
    maskMap: Map<string, CanvasObject>;
    isMaskMap: Set<string>;
    clippedGroups: Map<string, CanvasObject[]>;
  };
}

/**
 * 클립보드 관리 훅
 * 객체 복사/붙여넣기 기능 제공
 */
export function useClipboard({
  selectedId,
  getObjectById,
  addObject,
  setSelectedId,
  imageRegistry,
  requestRender,
  selectedIds,
  getSelectedObjects,
  setSelection,
  zOrder,
  setZOrder,
  setObjects,
  deleteObject,
  clearSelection,
  clipMaskMap
}: UseClipboardProps) {
  // 단일 객체 또는 다중 객체 배열 저장
  const clipboardRef = useRef<{
    objects: CanvasObject[];
    zOrderIndices: number[];  // 원본 zOrder에서의 인덱스들 (상대적 순서)
  } | null>(null);

  /**
   * 복사 기능
   * 선택된 객체를 클립보드에 저장
   */
  const handleCopy = useCallback(() => {
    // 다중 선택 모드 체크
    if (selectedIds && selectedIds.size > 1 && getSelectedObjects) {
      const selectedObjs = getSelectedObjects();
      if (selectedObjs.length === 0) return;
      
      // zOrder에서의 인덱스 저장 (상대적 순서 유지)
      const zOrderIndices = selectedObjs.map(obj => zOrder.indexOf(obj.id)).filter(idx => idx !== -1);
      
      // 객체들 깊은 복사
      const copiedObjects = selectedObjs.map(obj => {
        const copied: CanvasObject = JSON.parse(JSON.stringify(obj));
        if (obj.type === 'image' && obj.image) {
          // image 요소는 복사하지 않고 imageHash와 imageSrc만 유지
          copied.image = undefined;
          copied.imageSrc = obj.imageSrc;
          copied.imageHash = obj.imageHash;
        }
        return copied;
      });
      
      clipboardRef.current = {
        objects: copiedObjects,
        zOrderIndices
      };
    } else if (selectedId) {
      // 단일 선택 모드
      const obj = getObjectById(selectedId);
      if (!obj) return;
      
      // 객체를 깊은 복사하여 클립보드에 저장
      const copied: CanvasObject = JSON.parse(JSON.stringify(obj));
      if (obj.type === 'image' && obj.image) {
        copied.image = undefined;
        copied.imageSrc = obj.imageSrc;
        copied.imageHash = obj.imageHash;
      }
      
      clipboardRef.current = {
        objects: [copied],
        zOrderIndices: [zOrder.indexOf(selectedId)]
      };
    }
  }, [selectedId, selectedIds, getSelectedObjects, getObjectById, zOrder]);

  /**
   * 붙여넣기 기능
   * 클립보드의 객체를 새 ID로 복제하여 추가
   */
  const handlePaste = useCallback(() => {
    if (!clipboardRef.current) return;
    
    const { objects: copiedObjects, zOrderIndices } = clipboardRef.current;
    if (!copiedObjects || copiedObjects.length === 0) return;
    
    // 다중 선택 모드 (여러 객체 붙여넣기)
    if (copiedObjects.length > 1) {
      const newIds: string[] = [];
      const newObjects: CanvasObject[] = [];
      
      // 원본 zOrder 인덱스 순서대로 정렬
      const sortedObjects = copiedObjects.map((obj, index) => ({
        obj,
        originalIndex: zOrderIndices[index] ?? index
      })).sort((a, b) => a.originalIndex - b.originalIndex);
      
      // 원본 ID -> 새 ID 매핑 생성
      const idMap = new Map<string, string>();
      
      // 모든 객체를 새 ID로 복제
      sortedObjects.forEach(({ obj: copied }, index) => {
        const newId = `${copied.type}_${Date.now()}_${Math.floor(Math.random() * 1000)}_${index}`;
        idMap.set(copied.id, newId);
        
        // 새 객체 생성 (원래 위치에 붙여넣기)
        const newObj: CanvasObject = {
          ...copied,
          id: newId,
          x: Math.round(copied.x),
          y: Math.round(copied.y),
          // 클리핑 마스크 관계는 초기화 (나중에 복원)
          clipMaskId: undefined,
          isClipped: false
        };
        
        // 이미지 객체인 경우 이미지 레지스트리에서 복원
        if (newObj.type === 'image' && newObj.imageHash) {
          const entry = imageRegistry.getImage(newObj.imageHash);
          if (entry) {
            newObj.image = entry.image;
            newObj.imageSrc = entry.src;
          }
        }
        
        newObjects.push(newObj);
        newIds.push(newId);
      });
      
      // 클리핑 마스크 관계 복원
      newObjects.forEach(newObj => {
        const originalObj = sortedObjects.find(item => idMap.get(item.obj.id) === newObj.id)?.obj;
        if (originalObj && originalObj.clipMaskId) {
          // 원본 clipMaskId가 복사된 객체 중에 있으면 새 ID로 매핑
          const newMaskId = idMap.get(originalObj.clipMaskId);
          if (newMaskId) {
            newObj.clipMaskId = newMaskId;
            newObj.isClipped = true;
          }
          // 복사된 객체 중에 없으면 클리핑 관계 해제 (이미 undefined, false로 설정됨)
        }
      });
      
      // 모든 객체를 Map에 추가 (addObject는 zOrder에 자동 추가하므로 직접 추가)
      // addObject를 사용하지 않고 직접 objects와 zOrder를 업데이트
      setObjects(prev => {
        const next = new Map(prev);
        newObjects.forEach(obj => {
          next.set(obj.id, obj);
        });
        return next;
      });
      
      // zOrder 삽입 위치 결정
      let insertIndex: number;
      
      // 선택된 객체가 마스크/클리핑 레이어인지 확인
      const selectedObj = selectedId ? getObjectById(selectedId) : null;
      const selectedIsMask = selectedObj ? (clipMaskMap?.isMaskMap.has(selectedObj.id) ?? false) : false;
      const selectedIsClipped = selectedObj && selectedObj.isClipped && selectedObj.clipMaskId;
      
      if (selectedIsMask || selectedIsClipped) {
        // 선택된 객체가 마스크/클리핑 레이어인 경우: 속해있는 마스크 그룹의 위에 삽입
        let maskGroupTopIndex = -1;
        
        if (selectedIsMask && selectedObj) {
          // 선택된 객체가 마스크 객체: 마스크 그룹의 가장 위쪽 위치
          const maskIndex = zOrder.indexOf(selectedObj.id);
          if (maskIndex !== -1) {
            // 마스크 그룹의 가장 위쪽 위치 찾기 (마스크 객체 + 클리핑된 객체들 중 가장 위)
            const clippedObjects = clipMaskMap?.clippedGroups.get(selectedObj.id);
            let topIndex = maskIndex;
            if (clippedObjects) {
              clippedObjects.forEach(clipped => {
                const clippedIndex = zOrder.indexOf(clipped.id);
                if (clippedIndex !== -1 && clippedIndex > topIndex) {
                  topIndex = clippedIndex;
                }
              });
            }
            maskGroupTopIndex = topIndex + 1;
          }
        } else if (selectedIsClipped && selectedObj && selectedObj.clipMaskId) {
          // 선택된 객체가 클리핑된 객체: 속한 마스크 그룹의 가장 위쪽 위치
          const maskObj = clipMaskMap?.maskMap.get(selectedObj.clipMaskId);
          if (maskObj) {
            const maskIndex = zOrder.indexOf(maskObj.id);
            if (maskIndex !== -1) {
              // 마스크 그룹의 가장 위쪽 위치 찾기
              const clippedObjects = clipMaskMap?.clippedGroups.get(maskObj.id);
              let topIndex = maskIndex;
              if (clippedObjects) {
                clippedObjects.forEach(clipped => {
                  const clippedIndex = zOrder.indexOf(clipped.id);
                  if (clippedIndex !== -1 && clippedIndex > topIndex) {
                    topIndex = clippedIndex;
                  }
                });
              }
              maskGroupTopIndex = topIndex + 1;
            }
          }
        }
        
        if (maskGroupTopIndex !== -1) {
          insertIndex = Math.min(maskGroupTopIndex, zOrder.length);
        } else {
          // 마스크 그룹을 찾을 수 없으면 선택된 객체 기준으로 삽입
          insertIndex = selectedId ? zOrder.indexOf(selectedId) + 1 : zOrder.length;
        }
      } else {
        // 선택된 객체가 일반 객체: 선택된 객체의 바로 위에 삽입
        if (selectedId) {
          insertIndex = zOrder.indexOf(selectedId) + 1;
          // 선택된 객체가 zOrder에 없으면 맨 위에 삽입
          if (insertIndex === 0) {
            insertIndex = zOrder.length;
          }
        } else {
          // 선택된 객체가 없으면 맨 위에 삽입
          insertIndex = zOrder.length;
        }
      }
      
      setZOrder(prev => {
        const newZOrder = [...prev];
        // 상대적 순서 유지하면서 삽입
        newZOrder.splice(insertIndex, 0, ...newIds);
        return newZOrder;
      });
      
      // 다중 선택 설정
      if (setSelection) {
        setSelection(newIds);
      } else {
        setSelectedId(newIds[newIds.length - 1]);
      }
    } else {
      // 단일 선택 모드
      const copied = copiedObjects[0];
      const newId = `${copied.type}_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
      
      // 새 객체 생성
      const newObj: CanvasObject = {
        ...copied,
        id: newId,
        x: Math.round(copied.x),
        y: Math.round(copied.y),
        clipMaskId: undefined,
        isClipped: false
      };
      
      // 이미지 객체인 경우 이미지 레지스트리에서 복원
      if (newObj.type === 'image' && newObj.imageHash) {
        const entry = imageRegistry.getImage(newObj.imageHash);
        if (entry) {
          newObj.image = entry.image;
          newObj.imageSrc = entry.src;
        }
      }
      
      // 객체를 Map에 추가
      setObjects(prev => {
        const next = new Map(prev);
        next.set(newObj.id, newObj);
        return next;
      });
      
      // zOrder 삽입 위치 결정
      let insertIndex: number;
      
      // 선택된 객체가 마스크/클리핑 레이어인지 확인
      const selectedObj = selectedId ? getObjectById(selectedId) : null;
      const selectedIsMask = selectedObj ? (clipMaskMap?.isMaskMap.has(selectedObj.id) ?? false) : false;
      const selectedIsClipped = selectedObj && selectedObj.isClipped && selectedObj.clipMaskId;
      
      if (selectedIsMask || selectedIsClipped) {
        // 선택된 객체가 마스크/클리핑 레이어인 경우: 속해있는 마스크 그룹의 위에 삽입
        let maskGroupTopIndex = -1;
        
        if (selectedIsMask && selectedObj) {
          // 선택된 객체가 마스크 객체: 마스크 그룹의 가장 위쪽 위치
          const maskIndex = zOrder.indexOf(selectedObj.id);
          if (maskIndex !== -1) {
            // 마스크 그룹의 가장 위쪽 위치 찾기 (마스크 객체 + 클리핑된 객체들 중 가장 위)
            const clippedObjects = clipMaskMap?.clippedGroups.get(selectedObj.id);
            let topIndex = maskIndex;
            if (clippedObjects) {
              clippedObjects.forEach(clipped => {
                const clippedIndex = zOrder.indexOf(clipped.id);
                if (clippedIndex !== -1 && clippedIndex > topIndex) {
                  topIndex = clippedIndex;
                }
              });
            }
            maskGroupTopIndex = topIndex + 1;
          }
        } else if (selectedIsClipped && selectedObj && selectedObj.clipMaskId) {
          // 선택된 객체가 클리핑된 객체: 속한 마스크 그룹의 가장 위쪽 위치
          const maskObj = clipMaskMap?.maskMap.get(selectedObj.clipMaskId);
          if (maskObj) {
            const maskIndex = zOrder.indexOf(maskObj.id);
            if (maskIndex !== -1) {
              // 마스크 그룹의 가장 위쪽 위치 찾기
              const clippedObjects = clipMaskMap?.clippedGroups.get(maskObj.id);
              let topIndex = maskIndex;
              if (clippedObjects) {
                clippedObjects.forEach(clipped => {
                  const clippedIndex = zOrder.indexOf(clipped.id);
                  if (clippedIndex !== -1 && clippedIndex > topIndex) {
                    topIndex = clippedIndex;
                  }
                });
              }
              maskGroupTopIndex = topIndex + 1;
            }
          }
        }
        
        if (maskGroupTopIndex !== -1) {
          insertIndex = Math.min(maskGroupTopIndex, zOrder.length);
        } else {
          // 마스크 그룹을 찾을 수 없으면 선택된 객체 기준으로 삽입
          insertIndex = selectedId ? zOrder.indexOf(selectedId) + 1 : zOrder.length;
        }
      } else {
        // 선택된 객체가 일반 객체: 선택된 객체의 바로 위에 삽입
        if (selectedId) {
          insertIndex = zOrder.indexOf(selectedId) + 1;
          // 선택된 객체가 zOrder에 없으면 맨 위에 삽입
          if (insertIndex === 0) {
            insertIndex = zOrder.length;
          }
        } else {
          // 선택된 객체가 없으면 맨 위에 삽입
          insertIndex = zOrder.length;
        }
      }
      
      setZOrder(prev => {
        const newZOrder = [...prev];
        newZOrder.splice(insertIndex, 0, newId);
        return newZOrder;
      });
      
      // 새로 붙여넣은 객체 선택
      setSelectedId(newId);
    }
    
    requestRender();
  }, [imageRegistry, addObject, setSelectedId, setSelection, zOrder, setZOrder, selectedId, requestRender, getObjectById, clipMaskMap]);

  /**
   * 오려두기 기능
   * 선택된 객체를 클립보드에 복사하고 원본 삭제
   */
  const handleCut = useCallback(() => {
    // 먼저 복사 수행
    if (selectedIds && selectedIds.size > 1 && getSelectedObjects) {
      const selectedObjs = getSelectedObjects();
      if (selectedObjs.length === 0) return;
      
      // zOrder에서의 인덱스 저장 (상대적 순서 유지)
      const zOrderIndices = selectedObjs.map(obj => zOrder.indexOf(obj.id)).filter(idx => idx !== -1);
      
      // 객체들 깊은 복사
      const copiedObjects = selectedObjs.map(obj => {
        const copied: CanvasObject = JSON.parse(JSON.stringify(obj));
        if (obj.type === 'image' && obj.image) {
          // image 요소는 복사하지 않고 imageHash와 imageSrc만 유지
          copied.image = undefined;
          copied.imageSrc = obj.imageSrc;
          copied.imageHash = obj.imageHash;
        }
        return copied;
      });
      
      clipboardRef.current = {
        objects: copiedObjects,
        zOrderIndices
      };
      
      // 복사 후 원본 삭제
      const idsToDelete = Array.from(selectedIds);
      idsToDelete.forEach(id => {
        deleteObject(id);
      });
      
      // 선택 해제
      if (clearSelection) {
        clearSelection();
      }
      setSelectedId(null);
    } else if (selectedId) {
      // 단일 선택 모드
      const obj = getObjectById(selectedId);
      if (!obj) return;
      
      // 객체를 깊은 복사하여 클립보드에 저장
      const copied: CanvasObject = JSON.parse(JSON.stringify(obj));
      if (obj.type === 'image' && obj.image) {
        copied.image = undefined;
        copied.imageSrc = obj.imageSrc;
        copied.imageHash = obj.imageHash;
      }
      
      clipboardRef.current = {
        objects: [copied],
        zOrderIndices: [zOrder.indexOf(selectedId)]
      };
      
      // 복사 후 원본 삭제
      deleteObject(selectedId);
      setSelectedId(null);
    }
    
    requestRender();
  }, [selectedId, selectedIds, getSelectedObjects, getObjectById, zOrder, deleteObject, clearSelection, setSelectedId, requestRender]);

  return {
    handleCopy,
    handlePaste,
    handleCut
  };
}

